howspace.components.imageEditor = (() => {
    'use strict';

    /**
     * Saves the cropped image from the image editor component and dispatches an event with the cropped canvas data.
     * This function retrieves the cropped canvas from the image editor,
     * creates an event detail object containing the canvas data, and dispatches a custom event
     * to notify other components or listeners about the saved image.
     *
     * @param {jQuery} imageEditor - The jQuery object representing the image editor component.
     * @returns {void}
     */
    const saveImage = (imageEditor) => {
        const eventDetail = {
            data: howspace.components.imageEditor.getCroppedCanvas(imageEditor)
        }
        howspace.dispatchEvent(imageEditor[0], howspace.components.imageEditor.IMAGE_EDITOR_SAVE, eventDetail);
    }

    /**
     * Handles keydown events for the image editor component.
     * Supports arrow keys for moving the crop area, plus/minus keys for zooming,
     * and Enter key for saving the image.
     *
     * @param {KeyboardEvent} e - The keydown event object.
     * @returns {void}
     */
    const imageEditorKeydown = (e) => {
        const imageEditor = $(e.target).find('.howspace-image-editor');
        if (!imageEditor.is('.howspace-image-editor-initialized')) {
            return;
        }
        const cropper = imageEditor.data('cropper');
        if (!cropper) {
            return;
        }
        switch (e.key) {
            case 'ArrowLeft':
                e.preventDefault();
                cropper.move(-1, 0);
                break;
            case 'ArrowUp':
                e.preventDefault();
                cropper.move(0, -1);
                break;
            case 'ArrowRight':
                e.preventDefault();
                cropper.move(1, 0);
                break;
            case 'ArrowDown':
                e.preventDefault();
                cropper.move(0, 1);
                break;
            case '+':
            case '=':
            case 'Add':
                e.preventDefault();
                cropper.zoom(0.1);
                break;
            case '-':
            case 'Subtract':
                e.preventDefault();
                cropper.zoom(-0.1);
                break;
            case 'Enter':
                e.preventDefault();
                saveImage(imageEditor);
                break;
        }
    };

    const getFileUploaderForm = e => {
        const target = $(e.target);
        if (target.hasClass('howspace-file-uploader-form')) {
            return target;
        }
        const uploader = target.closest('.howspace-file-uploader-form');
        return uploader.length ? uploader : null;
    };

    const startFileProcessing = async (fileUploader, files) => {
        const imageEditor = fileUploader.closest('.howspace-image-editor');
        if (!imageEditor.length) {
            return;
        }

        const editorContainer = imageEditor.find('.image-editor-container');
        const imageContainer = editorContainer.find('.img-container');
        const buttonsContainer = editorContainer.find('.image-editor-buttons');
        const saveButton = editorContainer.find('.save-image-button');
        const sourceImage = imageContainer.find('img');
        if (sourceImage.length === 0) {
            return;
        }

        howspace.animate(fileUploader, 'slideUp', 350, () => {
            fileUploader.addClass('d-none');
            const loader = `<div class="howspace-loader howspace-lister-loader medium"></div>`;
            editorContainer.before(loader);
            editorContainer.removeClass('d-none');

            howspace.animate(editorContainer, 'slideDown', 350, async () => {
                const file = files[0];
                if (!file?.type?.startsWith('image/')) {
                    return;
                }

                let imageBlob = file;
                if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
                    try {
                        const { heicTo } = await import(lib.getSrc('heic-to', 'heic-to.min.js'));
                        const heifImage = await heicTo({
                            blob: file,
                            type: "image/jpeg",
                            quality: 0.9
                        });
                        if (!heifImage) {
                            return;
                        }
                        imageBlob = heifImage;
                    } catch (err) {
                        real.notification.show(howspace.translate('common_heic_image_conversion_failed'), 'danger');
                        return;
                    }
                }

                const reader = new FileReader();
                reader.onload = (e) => {
                    imageEditor.find('.howspace-loader').remove();
                    imageContainer.removeClass('d-none');
                    buttonsContainer.removeClass('d-none');
                    saveButton.parent().removeClass('d-none');
                    sourceImage.attr('src', e.target.result);
                    initImageEditor(imageEditor);
                };
                reader.readAsDataURL(imageBlob);
            });
        });
    };

    const handleClick = e => {
        const fileUploader = getFileUploaderForm(e);
        fileUploader.find('.howspace-file-input').trigger('click');
    };

    const handleChange = e => {
        const fileUploader = getFileUploaderForm(e);
        startFileProcessing(fileUploader, e.target.files);
    };

    const handleDrag = e => {
        e.preventDefault();
    };

    const handleDragOver = e => {
        const fileUploader = getFileUploaderForm(e);
        if (!fileUploader.hasClass('uploading')) {
            fileUploader.addClass('drag-over');
        }
    };

    const handleDragLeave = e => {
        const fileUploader = getFileUploaderForm(e);
        fileUploader.removeClass('drag-over');
    };

    const handleDrop = e => {
        let dataTransfer = null;
        if (e?.dataTransfer) {
            dataTransfer = e.dataTransfer;
        }

        if (e?.originalEvent?.dataTransfer) {
            dataTransfer = e.originalEvent.dataTransfer;
        }

        if (dataTransfer?.files?.length <= 0) {
            return;
        }

        const fileUploader = getFileUploaderForm(e);
        fileUploader.removeClass('drag-over');
        const files = dataTransfer.files;
        startFileProcessing(fileUploader, files);
    };

    const handlePaste = (e) => {
        e.preventDefault();
        let clipboardData = null;

        if (e?.clipboardData) {
            clipboardData = e.clipboardData;
        }

        if (e?.originalEvent?.clipboardData) {
            clipboardData = e.originalEvent.clipboardData;
        }

        if (window.clipboardData) {
            clipboardData = window.clipboardData;
        }

        if (!clipboardData?.items) {
            return;
        }

        const fileUploader = getFileUploaderForm(e);
        const items = clipboardData.items;
        const files = [];

        for (const item of items) {
            if (item.kind === 'file') {
                files.push(item.getAsFile());
            }
        }

        if (files.length > 0) {
            startFileProcessing(fileUploader, files);
        }
    };

    const initImageEditor = (imageEditor, options = {}) => {
        const imageContainer = imageEditor.find('.img-container');
        const image = imageContainer.find('img');
        if (image.length == 0 || !image.attr('src')) {
            return;
        }

        imageEditor.addClass('cropper-initializing');
        howspace.dispatchEvent(imageEditor, howspace.components.imageEditor.IMAGE_EDITOR_INITIALIZING);

        const defaultOptions = {
            aspectRatio: 1,
            viewMode: 1,
            minCropBoxWidth: 150,
            minCropBoxHeight: 150,
            ready: function (e) {
                imageEditor.removeClass('cropper-initializing').addClass('cropper-initialized');
                howspace.dispatchEvent(imageEditor, howspace.components.imageEditor.IMAGE_EDITOR_READY, e);
            },
            cropstart: function (e) {
                howspace.dispatchEvent(imageEditor, howspace.components.imageEditor.IMAGE_EDITOR_CROP_START, e);
            },
            cropmove: function (e) {
                howspace.dispatchEvent(imageEditor, howspace.components.imageEditor.IMAGE_EDITOR_CROP_MOVE, e);
            },
            cropend: function (e) {
                howspace.dispatchEvent(imageEditor, howspace.components.imageEditor.IMAGE_EDITOR_CROP_END, e);
            },
            crop: function (e) {
                howspace.dispatchEvent(imageEditor, howspace.components.imageEditor.IMAGE_EDITOR_CROP, e);
            },
            zoom: function (e) {
                howspace.dispatchEvent(imageEditor, howspace.components.imageEditor.IMAGE_EDITOR_ZOOM, e);
            },
        };

        const imageEditorOptions = $.extend({}, defaultOptions, options);

        const cropper = new Cropper(image[0], imageEditorOptions);

        imageEditor.data('cropper', cropper);

        imageEditor.find('.image-editor-button').on('click', e => {
            const btn = $(e.target);
            const cropper = imageEditor.data('cropper');

            if (!btn.data('method') || !btn.data('option')) {
                return;
            }

            const method = btn.data('method');
            const option = btn.data('option');

            cropper[method](option);

            switch (method) {
                case 'rotate':
                    // It finalizes the cropping selection and updates the image accordingly based on the currently selected cropping area.
                    // to update the cropping area after rotation
                    if (cropper && imageEditorOptions.viewMode > 0) {
                        cropper.crop();
                    }
                    break;

                // Set the opposite value to the data attribute since it will be set afterwards
                case 'scaleX':
                case 'scaleY':
                    btn.dataAndAttr('option', -option);
                    break;
            }
        });

        imageEditor.find('.save-image-button').on('click', (e) => {
            real.button.loading($(e.target), imageEditor);
            saveImage(imageEditor);
        });

        document.body.addEventListener('keydown', imageEditorKeydown, true);
    };

    /**
     * Destroys the image editor instance if it has been initialized.
     * Removes the 'howspace-image-editor-initialized' class, destroys the cropper instance,
     * removes cropper data, and detaches the keydown event listener from the document body.
     *
     * @param {jQuery} imageEditor - The jQuery element representing the image editor container.
     */
    const destroyImageEditor = (imageEditor) => {
        if (!imageEditor.is('.howspace-image-editor-initialized')) {
            return;
        }
        imageEditor.removeClass('howspace-image-editor-initialized');
        const cropper = imageEditor.data('cropper');
        if (cropper) {
            cropper.destroy();
            imageEditor.removeData('cropper');
            document.body.removeEventListener('keydown', imageEditorKeydown, true);
        }
    }

    return {
        IMAGE_EDITOR_INITIALIZING: 'image-editor-initializing',
        IMAGE_EDITOR_READY: 'image-editor-ready',
        IMAGE_EDITOR_CROP_START: 'image-editor-crop-start',
        IMAGE_EDITOR_CROP_MOVE: 'image-editor-crop-move',
        IMAGE_EDITOR_CROP_END: 'image-editor-crop-end',
        IMAGE_EDITOR_CROP: 'image-editor-crop',
        IMAGE_EDITOR_ZOOM: 'image-editor-zoom',
        IMAGE_EDITOR_SAVE: 'image-editor-save',
        init: (elem, options) => {
            elem.findIncludingSelf('.howspace-image-editor:not(.howspace-image-editor-initialized)').each(function () {
                const imageEditor = $(this);
                imageEditor.addClass('howspace-image-editor-initialized');

                const uploaderForm = imageEditor.find('.image-editor-uploader-form');

                // When clicking the button -> trigger click on the file input.
                const fileButton = uploaderForm.find('.howspace-file-button');
                fileButton.on('click', handleClick);

                // Browsing files.
                const fileInput = uploaderForm.find('.howspace-file-input');
                fileInput.on('change', handleChange);

                // Pasting files.
                uploaderForm.on('paste', handlePaste);

                // Drag events.
                ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach(dragEvent => {
                    uploaderForm.on(dragEvent, handleDrag);
                });

                // Drag over events.
                ['dragover', 'dragenter'].forEach(dragOverEvent => {
                    uploaderForm.on(dragOverEvent, handleDragOver);
                });

                // Drag leave events.
                ['dragleave', 'dragend'].forEach(dragLeaveEvent => {
                    uploaderForm.on(dragLeaveEvent, handleDragLeave);
                });

                // Drop.
                uploaderForm.on('drop', handleDrop);
            });
        },

        getCroppedCanvas: (imageEditor) => {
            const cropper = imageEditor.data('cropper');
            if (!cropper) {
                return;
            }

            return cropper.getCroppedCanvas();
        },

        destroy: (elem) => {
            elem.findIncludingSelf('.howspace-image-editor.howspace-image-editor-initialized').each(function () {
                const imageEditor = $(this);
                imageEditor.removeClass('howspace-image-editor-initialized');

                destroyImageEditor(imageEditor);

                const uploaderForm = imageEditor.find('.image-editor-uploader-form');
                // Remove event listeners
                uploaderForm.find('.howspace-file-button').off('click');
                uploaderForm.find('.howspace-file-input').off('change');
                uploaderForm.off('paste');
                uploaderForm.off('drag');
                uploaderForm.off('dragstart');
                uploaderForm.off('dragend');
                uploaderForm.off('dragover');
                uploaderForm.off('dragenter');
                uploaderForm.off('dragleave');
                uploaderForm.off('drop');
            });
        },
    };

})();
